import { DataGroupEnum } from "@/components/refineRealEstate/dataGroupsConstants";

import { tealiumTracker } from "./tealium-tracker";
import { getTrackingParams } from "./tracking-helpers";

export type TrackingReportType = undefined | "UTAG_LINK" | "UTAG_VIEW";
export type TrackEventParams = {
  pag_pagetitle?: string;
  event_name?: string;
  event_product?: string;
  obj_scoutid?: string;
  event_parameter_1?: string;
  event_parameter_2?: string;
  event_parameter_3?: string;
  event_parameter_4?: string;
};
export type TrackingFunction = (
  event: Record<string, unknown>,
  additional?: {
    reportType: TrackingReportType;
  },
) => void;

const tracker = tealiumTracker();

export const executeTracking = (event: TrackEventParams, type?: TrackingReportType): Promise<void> => {
  return tracker
    .then((reportingFunction) => {
      if (type != null) {
        reportingFunction(event, { reportType: type });
      } else {
        reportingFunction(event);
      }
    })
    .catch(console.error);
};

export function trackTealiumEvent(
  params: Omit<TrackEventParams, "pag_pagetitle">,
  options?: { includeRealEstateId?: boolean },
): Promise<void> {
  const { pageTitle, realEstateId } = getTrackingParams();
  const trackingParams = {
    ...params,
    pag_pagetitle: pageTitle,
    ...(options?.includeRealEstateId && { obj_scoutid: realEstateId }),
  };
  return executeTracking(trackingParams);
}

export function trackPageView(suffix?: string | DataGroupEnum): Promise<void> {
  const { pageTitle, realEstateId } = getTrackingParams();

  // Map DataGroupEnum to tracking-friendly names
  const DATA_GROUP_TRACKING_MAP: Record<DataGroupEnum, string> = {
    [DataGroupEnum.generalInfo]: "general_data",
    [DataGroupEnum.plotData]: "plot_data",
    [DataGroupEnum.buildingData]: "building_data",
    [DataGroupEnum.energyAndPhotovoltaic]: "energy_data",
    [DataGroupEnum.buildingStandard]: "building_features",
    [DataGroupEnum.modernization]: "modernization_data",
    [DataGroupEnum.financialAndRentalInfo]: "financial_data",
    [DataGroupEnum.propertyImages]: "photos",
  };

  let mappedSuffix = suffix;
  if (suffix && Object.values(DataGroupEnum).includes(suffix as DataGroupEnum)) {
    mappedSuffix = DATA_GROUP_TRACKING_MAP[suffix as DataGroupEnum];
  }

  const finalPageTitle = mappedSuffix ? `${pageTitle}.${mappedSuffix}` : pageTitle;

  const trackingParams = {
    pag_pagetitle: finalPageTitle,
    ...(realEstateId && { obj_scoutid: realEstateId }),
  };

  return executeTracking(trackingParams, "UTAG_VIEW");
}
